import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Container, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import api, { ENGINE } from '../../infrastructure/config/api';
import Button from '../components/common/Button';
import EngineRunsTable from '../components/engine/EngineRunsTable';

const formatCountdown = (remainingMs) => {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const getNextQuarterHourTime = (fromDate = new Date()) => {
  const nextDate = new Date(fromDate);
  const utcMinutes = nextDate.getUTCMinutes();
  const minuteRemainder = utcMinutes % 15;
  const minutesToAdd = minuteRemainder === 0 ? 15 : 15 - minuteRemainder;

  nextDate.setUTCSeconds(0, 0);
  nextDate.setUTCMinutes(utcMinutes + minutesToAdd, 0, 0);
  return nextDate.getTime();
};

const EngineRuns = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [symbol, setSymbol] = useState('BTCUSDT');
  const [limit, setLimit] = useState(50);
  const [userId, setUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [remainingMs, setRemainingMs] = useState(() => getNextQuarterHourTime() - Date.now());

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch (error) {
      return {};
    }
  }, []);

  const isAdminView = currentUser.role === 'admin' || currentUser.role === 'founder';

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        symbol: symbol || undefined,
        limit,
      };

      if (isAdminView && userId) {
        params.userId = Number(userId);
      }

      const response = await api.get(ENGINE.RUNS, { params });
      setRows(response.data?.data || []);
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || 'Failed to load engine runs', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, isAdminView, limit, symbol, userId]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  useEffect(() => {
    const updateRemaining = () => {
      const nextRemainingMs = getNextQuarterHourTime() - Date.now();

      if (nextRemainingMs <= 0) {
        window.location.reload();
        return;
      }

      setRemainingMs(nextRemainingMs);
    };

    updateRemaining();
    const intervalId = window.setInterval(() => {
      updateRemaining();
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Engine Runs</Typography>
        <Typography variant="body2" color="text.secondary">
          Reload sau: {formatCountdown(remainingMs)}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          label="Symbol"
          size="small"
          value={symbol}
          onChange={(e) => setSymbol(String(e.target.value || '').toUpperCase())}
        />
        <TextField
          label="Limit"
          size="small"
          type="number"
          value={limit}
          onChange={(e) => setLimit(Math.max(1, Math.min(200, Number(e.target.value) || 50)))}
        />
        {isAdminView && (
          <TextField
            label="User ID (optional)"
            size="small"
            type="number"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          />
        )}
        <Button variant="contained" onClick={fetchRuns} loading={loading}>Refresh</Button>
      </Box>

      <EngineRunsTable rows={rows} />
    </Container>
  );
};

export default EngineRuns;
